// OCT-7341: TreatyApp: File Activity panel — add search, filters, and a count of matching activities - Frontend : ashish.atwal@resolutionlife.com
// Renders the (already filtered) list of activity events, grouped by date, with
// match highlighting driven by the current search term. Loading/error/empty
// states and the "Show more" pagination are handled here; the actual data
// loading, filtering and searching are owned by the FileActivityPanel and passed
// in as props, so all filtering happens client-side over the loaded events.
import { Children, FC, memo, useCallback } from 'react';
import { Box, Button, Skeleton, Stack } from '@chakra-ui/react';
import { useSplunkActivityLogger } from '@resolutionlife/ui';
import { isEmpty, repeat, takeLast } from 'ramda';
import { useNavigate } from 'react-router-dom';

import { EventFragment } from '../../../graphql/generated';
import { navigateToDirectory } from '../../../pages/file-browser-page/file-browser-page';
import { navigateToFileDetailsRoute } from '../../../pages/file-details-page/file-details-page';
import { DebounceLoadingState } from '../../../ui/debounce-loading/debounce-loading';
import { EmptyState } from '../../../ui/errors/empty-state';
import { ErrorState } from '../../../ui/errors/error-state';

import {
  getDateLabel,
  groupByDate,
} from '../../file-activity-panel/format-events';
import { DateHeader } from '../date-header';
import { Event } from '../event';

type Props = {
  cedant: string;
  cedantName: string;
  onClose: () => void;
  // The already-filtered events to render.
  events: EventFragment[];
  // Whether any search/filter is currently applied (drives the empty-state copy).
  isFiltered: boolean;
  // The current search term used to highlight matches.
  searchTerm?: string;
  loading: boolean;
  error: boolean;
  isFetchingMore: boolean;
  hasMore?: boolean;
  fetchMore: (cursor: string) => () => void;
};

export const FileActivityList: FC<Props> = ({
  cedant,
  cedantName,
  onClose,
  events,
  isFiltered,
  searchTerm,
  loading,
  error,
  isFetchingMore,
  hasMore,
  fetchMore,
}) => {
  const navigate = useNavigate();
  const activityLogger = useSplunkActivityLogger();

  const handleNavigateToParentFolder = useCallback(
    (event: EventFragment) => () => {
      onClose();

      const parentDirPath = navigateToDirectory({
        cedantId: cedant,
        directoryId: event.storageObject.parentDirectoryId,
      });

      navigate(parentDirPath);
    },
    [cedant, navigate, onClose]
  );

  const handleNavigateToFolder = useCallback(
    (event: EventFragment) => () => {
      onClose();

      const dirPath = navigateToDirectory({
        cedantId: cedant,
        directoryId: event.storageObject.id,
      });

      navigate(dirPath);
    },
    [cedant, navigate, onClose]
  );

  const handleNavigateToFile = useCallback(
    (event: EventFragment) => () => {
      onClose();

      const fileDetailsPath = navigateToFileDetailsRoute({
        cedantId: cedant,
        fileId: event.storageObject.id,
      });

      navigate(fileDetailsPath);
    },
    [cedant, navigate, onClose]
  );

  if (loading && !isFetchingMore) {
    return (
      <DebounceLoadingState>
        <Stack flex={1} spacing="6">
          {Children.toArray(repeat(<Skeleton height="20" />, 8))}
        </Stack>
      </DebounceLoadingState>
    );
  }

  if (error) {
    return (
      <ErrorState
        title="Unable to load file activity"
        subtitle="Please reach out to the technical support team for further assistance."
      />
    );
  }

  if (isEmpty(events)) {
    return (
      <EmptyState
        title={
          isFiltered
            ? 'No activity matches the selected filters'
            : 'No file activity to display'
        }
        subtitle={
          isFiltered
            ? undefined
            : 'Events will appear once files have been uploaded.'
        }
      />
    );
  }

  const groupedEvents = groupByDate(events);
  const eventsTuple = Object.entries(groupedEvents);
  const nextPageCursor = takeLast(1, events)[0].id;
  return (
    <Stack spacing="8" data-testid="activity-feed-list">
      {eventsTuple.map(([date, eventsGroup]) => (
        <Box key={date}>
          <DateHeader title={getDateLabel(date)} />
          <Stack spacing="4" pt="4">
            {eventsGroup.map((event) => (
              <Event
                key={event.id}
                event={event}
                cedantName={cedantName}
                searchTerm={searchTerm}
                onClickFile={handleNavigateToFile}
                onClickFolder={handleNavigateToFolder}
                onClickParentFolder={handleNavigateToParentFolder}
              />
            ))}
          </Stack>
        </Box>
      ))}
      {hasMore && (
        <Button
          isLoading={isFetchingMore}
          data-testid="fetch-more-btn"
          onClick={() => {
            activityLogger.logActivityFeedPaginate({ cedantId: cedant, cursor: nextPageCursor });
            fetchMore(nextPageCursor)();
          }}
        >
          Show more
        </Button>
      )}
    </Stack>
  );
};

export const MemoizedFileActivityList = memo(FileActivityList);
